import {
  AccordionDetails,
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableHead,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  StyledButton,
  StyledTableCellHistory,
  StyledTableContainer,
  StyledTableRowHistory,
  StyledTextField,
  theme,
  UnderwriterStyledAccordion,
} from "../doctor-console/StyledComponents";
import { ThemeProvider } from "@mui/material/styles";
import Icon from "../doctor-console/Utils/Icons";
import axios from "axios";
import { toast } from "react-toastify";
import { displayPatientName } from "../../utils/displayPatientName";

const unitOptions = ["mg/dL", "g/dL", "mmol/L", "cells/mcL", "µg/L", "L", "%"];
// helper id generator
const genId = () =>
  `${Date.now().toString(36)}_${Math.floor(Math.random() * 10000)}`;

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

const DiagnosticReportsUnderwriter = ({
  initialData,
  setAiResponse,
  selectedPatient,
  handleRefresh,
}) => {
  const [panelEntries, setPanelEntries] = useState([]); // panelEntries is the only editable state while user types
  const [expandedPanels, setExpandedPanels] = useState({});
  const [authToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );

  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  // Initialize panelEntries only once on mount (or when initialData changes intentionally)
  useEffect(() => {
    if (!initialData?.file_data?.length) return;

    const detail = initialData.file_data[0].detail || [];
    const panels = detail
      .map((d, reportIndex) => {
        if (d.resource_type !== "DiagnosticReport") return null;
        const observations = Array.isArray(d.test_panel_observations)
          ? d.test_panel_observations
          : [];
        return {
          // basic panel metadata
          id: genId(), // <-- stable id for the panel
          panel_name: d.test_panel_name ?? `Panel ${reportIndex + 1}`,
          reportIndex, // keep original index to map back on submit
          test_report_date: d.test_report_date ?? null,
          diagnostic_date: d.diagnostic_date ?? null,
          conclusion: d.conclusion ?? "",
          // tests array for editing
          tests: observations.map((obs) => ({
            id: genId(),
            observation_description: obs.observation_description ?? "",
            value: obs.value_quantity ?? obs.value_string ?? "",
            unit:
              (obs.value_quantity && obs.value_quantity.unit) ?? obs.unit ?? "",
            // we keep original observation object (optional) if needed
            _originalObservation: obs,
          })),
        };
      })
      .filter(Boolean);

    setPanelEntries(panels);
  }, [initialData]); // only re-run when initialData prop changes

  // ---------- Editable handlers (update panelEntries only) ----------
  const handlePanelNameChange = (panelIndex, newName) => {
    setPanelEntries((prev) => {
      const next = [...prev];
      next[panelIndex] = { ...next[panelIndex], panel_name: newName };
      return next;
    });
  };

  const handleTestChange = (panelIndex, testId, field, newValue) => {
    setPanelEntries((prev) =>
      prev.map((p, pi) =>
        pi !== panelIndex
          ? p
          : {
            ...p,
            tests: p.tests.map((t) =>
              t.id === testId ? { ...t, [field]: newValue } : t
            ),
          }
      )
    );
  };

  const handleAddTest = (panelIndex) => {
    setPanelEntries((prev) => {
      const next = [...prev];
      const newTest = {
        id: genId(),
        observation_description: "",
        value: "",
        unit: "",
        _originalObservation: null,
      };
      next[panelIndex] = {
        ...next[panelIndex],
        tests: [...next[panelIndex].tests, newTest],
      };
      return next;
    });
  };

  const handleDeleteTest = (panelIndex, testId) => {
    setPanelEntries((prev) =>
      prev.map((p, pi) =>
        pi !== panelIndex
          ? p
          : { ...p, tests: p.tests.filter((t) => t.id !== testId) }
      )
    );
  };

  const handleAddPanel = (panelName = "New Panel") => {
    setPanelEntries((prev) => [
      ...prev,
      {
        id: genId(), // <-- stable id for the panel
        panel_name: panelName,
        reportIndex: null, // new panel not present in original detail; mark null to append on submit
        test_report_date: null,
        diagnostic_date: null,
        conclusion: "",
        tests: [
          {
            id: genId(),
            observation_description: "",
            value: "",
            unit: "",
            _originalObservation: null,
          },
        ],
      },
    ]);
  };

  const handleDeletePanel = (panelIndex) => {
    setPanelEntries((prev) => prev.filter((_, i) => i !== panelIndex));
  };

  // ---------- Submit: map panelEntries back into the original `data` shape ----------
  const handleSubmit = async () => {
    // ---------- Validation (block submit) ----------
    if (!Array.isArray(panelEntries) || panelEntries.length === 0) {
      toast.error(
        "Cannot submit: please add at least one Test Panel before submitting."
      );
      return;
    }

    for (let pIndex = 0; pIndex < panelEntries.length; pIndex++) {
      const panel = panelEntries[pIndex];
      const panelLabel = !isBlank(panel?.panel_name)
        ? panel.panel_name
        : `Panel ${pIndex + 1}`;

      if (!Array.isArray(panel?.tests) || panel.tests.length === 0) {
        toast.error(
          `Cannot submit: "${panelLabel}" has no tests. Please add at least one test.`
        );
        return;
      }

      for (let tIndex = 0; tIndex < panel.tests.length; tIndex++) {
        const test = panel.tests[tIndex];
        const testName = test?.observation_description ?? "";

        // Requirement: if test name is present, value is mandatory
        if (!isBlank(testName) && isBlank(test?.value)) {
          toast.error(
            `Cannot submit: please enter a value for "${String(testName).trim()}" in "${panelLabel}".`
          );
          return;
        }
      }
    }

    // Deep clone initialData (structuredClone if available)
    let finalData;
    try {
      finalData =
        typeof structuredClone === "function"
          ? structuredClone(initialData)
          : JSON.parse(JSON.stringify(initialData));
    } catch (err) {
      finalData = JSON.parse(JSON.stringify(initialData));
    }

    // Ensure file_data[0].detail exists
    finalData = finalData || {};
    finalData.file_data = finalData.file_data || [{}];
    finalData.file_data[0].detail = finalData.file_data[0].detail || [];

    // Overwrite identifiers based on selected patient context
    if (selectedPatient?.care_context) {
      finalData.file_data[0].careContextID = selectedPatient.care_context;
    }
    if (selectedPatient?.patient_id) {
      finalData.file_data[0].patientID = selectedPatient.patient_id;
    }

    const originalDetail = finalData.file_data[0].detail;

    // We'll build a newDetail array by walking panelEntries
    const newDetail = panelEntries.map((panel) => {
      // If panel.reportIndex is not null and exists in originalDetail, keep other fields, else create fresh DiagnosticReport skeleton
      const isExisting =
        typeof panel.reportIndex === "number" &&
        originalDetail[panel.reportIndex] &&
        originalDetail[panel.reportIndex].resource_type === "DiagnosticReport";

      const base = isExisting
        ? { ...originalDetail[panel.reportIndex] }
        : {
          resource_type: "DiagnosticReport",
          test_panel_code: null,
          test_panel_name: panel.panel_name,
          test_report_date:
            panel.test_report_date ?? new Date().toISOString().split("T")[0],
          diagnostic_date:
            panel.diagnostic_date ?? new Date().toISOString().split("T")[0],
          conclusion: panel.conclusion ?? "",
          test_panel_observations: [],
        };

      // Replace/overwrite fields we allow editing
      base.test_panel_name = panel.panel_name;
      base.test_report_date = panel.test_report_date ?? base.test_report_date;
      base.diagnostic_date = panel.diagnostic_date ?? base.diagnostic_date;
      base.conclusion = panel.conclusion ?? base.conclusion;

      // Build observations from panel.tests
      base.test_panel_observations = panel.tests.map((t) => {
        // If original exists and we want to preserve other metadata, we can merge; else create
        const obs = t._originalObservation
          ? { ...t._originalObservation }
          : {
            observation_code: null,
            observation_date: new Date().toISOString().split("T")[0],
            observation_description: "",
            unit: t.unit || "",
            unit_code: null,
            value_boolean: null,
            value_codeable_concept: null,
            value_quantity: null,
            value_string: null,
          };

        // set what user edited
        obs.observation_description =
          t.observation_description ?? obs.observation_description;
        // prefer putting value into value_quantity (matching backend sample shape)
        if (
          t.value === "" ||
          t.value === null ||
          typeof t.value === "undefined"
        ) {
          obs.value_quantity = null;
          obs.value_string = obs.value_string ?? "";
        } else {
          // try to convert numeric strings to numbers if appropriate
          const maybeNum = Number(t.value);
          if (Number.isFinite(maybeNum)) {
            obs.value_quantity = maybeNum;
            obs.value_string = null;
          } else {
            // keep non-numeric values as string in both fields (per sample: "n.b.")
            obs.value_quantity = t.value;
            obs.value_string = String(t.value);
          }
        }

        // unit
        obs.unit = t.unit || "";

        return obs;
      });

      return base;
    });

    // Replace DiagnosticReports in finalData.file_data[0].detail.
    // Option A: overwrite ENTIRE detail with newDetail *and preserve non-DiagnosticReport entries*.
    const preservedNonDiag = originalDetail.filter(
      (d) => d.resource_type !== "DiagnosticReport"
    );
    finalData.file_data[0].detail = [...preservedNonDiag, ...newDetail].map(
      (d) => {
        if (d?.resource_type === "Encounter" && selectedPatient?.care_context) {
          return { ...d, encounter_id: selectedPatient.care_context };
        }
        return d;
      }
    );

    console.log("Final data to submit:", finalData);

    const payload = {
      "HIP": finalData?.HIP,
      "file_data": finalData?.file_data,
      "job_id": selectedPatient?.job_id
    }

    try {
      const resp = await axios
        .post(`${REACT_APP_BACKEND_ROUTE_URL}submit_diagnostic_report/`, payload, {
          headers: headers,
        });
      console.log("Submit response:", resp.data);
      if (resp.data.status === "Success") {
        handleRefresh();
        toast.success("Diagnostic report submitted successfully. The status of the report will be updated shortly.");
        setAiResponse(null)
      }
      else {
        toast.error(resp.data.message);
      }
    } catch (err) {
      console.error("Submit error:", err);
    }

  };

  return (
    <div>
      {initialData && (
        <ThemeProvider theme={theme}>
          <Paper sx={{ padding: 3, marginTop: 3 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
              }}
            >
              {/* ID Box */}
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  backgroundColor: "#E9F5FE",
                  borderRadius: 1.5,
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="primary" fontWeight={600}>
                  ID: {selectedPatient?.patient_id}
                </Typography>
              </Paper>

              {/* Name Box */}
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  backgroundColor: "#E9F5FE",
                  borderRadius: 1.5,
                  p: 2,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2" color="primary" fontWeight={600}>
                  Name: {displayPatientName(selectedPatient?.patient_name)}
                </Typography>
              </Paper>
            </Box>
            {/* Buttons */}
            <Box
              sx={{
                marginBottom: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <StyledButton
                bgColor="#EEEEEE"
                textColor="#4D4D4D"
                variant="contained"
                onClick={() => setAiResponse(null)}
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.8rem" },
                }}
              >
                Back
              </StyledButton>
              <StyledButton
                bgColor="#125386"
                variant="contained"
                onClick={() => handleAddPanel("New Panel")}
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.8rem" },
                }}
              >
                Add Panel
              </StyledButton>
              <StyledButton
                bgColor="#125386"
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  fontSize: { xs: "0.6rem", sm: "0.8rem" },
                }}
              >
                Submit
              </StyledButton>
            </Box>
            {/* Panels with tests */}
            {panelEntries.map((panel, panelIndex) => (
              <div
                style={{
                  border: "1px solid #808285",
                  borderRadius: "10px",
                  padding: "6px",
                  marginBottom: "0.75rem",
                }}
              >
                <UnderwriterStyledAccordion
                  key={panel.id} // <-- stable key (NOT panel.panel_name)
                  expanded={!!expandedPanels[panel.id]} // <-- using id for expanded state
                  onChange={() =>
                    setExpandedPanels((prev) => ({
                      ...prev,
                      [panel.id]: !prev[panel.id], // toggle individually
                    }))
                  }
                  sx={{
                    borderRadius: 1,
                  }}
                  headerContent={
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      wrap="nowrap"
                      // forward clicks from any non-interactive area of the header to toggle accordion
                      onClick={(e) => {
                        // if click started on an interactive element, don't toggle
                        if (
                          e.target.closest("input,button,textarea,select,svg")
                        )
                          return;
                        // toggle expansion for this panel
                        setExpandedPanels((prev) => ({
                          ...prev,
                          [panel.id]: !prev[panel.id],
                        }));
                      }}
                      // onMouseDown helps prevent some browsers from toggling before input focus, optional
                      onMouseDown={(e) => {
                        if (
                          e.target.closest("input,button,textarea,select,svg")
                        )
                          return;
                        // do nothing else — actual toggle on click
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      {/* Title area: show input when expanded, plain text when collapsed */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flex: 1,
                        }}
                      // protect interactive input from propagation on its own events
                      // (we still rely on the Grid-level guard above)
                      >
                        {expandedPanels[panel.id] ? (
                          <Grid item xs={12} lg={8}>
                            <StyledTextField
                              size="small"
                              value={panel.panel_name}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              fullWidth
                              onChange={(e) =>
                                handlePanelNameChange(
                                  panelIndex,
                                  e.target.value
                                )
                              }
                              inputProps={{ style: { width: "100%" } }}
                            />
                          </Grid>
                        ) : (
                          <Typography
                            sx={{
                              color: "#176BAD",
                              fontSize: "16px",
                              fontStyle: "normal",
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={panel.panel_name}
                          >
                            {panel.panel_name || "Untitled Panel"}
                          </Typography>
                        )}
                      </div>

                      {/* Delete button — keep it independent; it will stop propagation */}
                      <Tooltip title="Delete Panel">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePanel(panelIndex);
                          }}
                        >
                          <Icon name="deletePanelInDiagnostic" size={24} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  }
                >
                  <AccordionDetails>
                    <StyledTableContainer
                      component={Paper}
                      sx={{
                        border: "1px solid #ccc",
                        overflowX: "auto",
                        maxWidth: "100%",
                      }}
                    >
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <StyledTableRowHistory>
                            <StyledTableCellHistory sx={{ width: "45%" }}>
                              Test Name
                            </StyledTableCellHistory>

                            <StyledTableCellHistory sx={{ width: "25%" }}>
                              Value
                            </StyledTableCellHistory>
                            <StyledTableCellHistory sx={{ width: "25%" }}>
                              Unit
                            </StyledTableCellHistory>
                            <StyledTableCellHistory sx={{ width: "5%" }}>
                              Actions
                            </StyledTableCellHistory>
                          </StyledTableRowHistory>
                        </TableHead>

                        <TableBody>
                          {panel.tests.map((testRow) => (
                            <StyledTableRowHistory key={testRow.id}>
                              <StyledTableCellHistory sx={{ width: "45%" }}>
                                {/* Editable test name */}
                                <StyledTextField
                                  size="small"
                                  fullWidth
                                  value={testRow.observation_description}
                                  onChange={(e) =>
                                    handleTestChange(
                                      panelIndex,
                                      testRow.id,
                                      "observation_description",
                                      e.target.value
                                    )
                                  }
                                />
                              </StyledTableCellHistory>

                              <StyledTableCellHistory sx={{ width: "25%" }}>
                                <StyledTextField
                                  value={testRow.value}
                                  size="small"
                                  fullWidth
                                  onChange={(e) =>
                                    handleTestChange(
                                      panelIndex,
                                      testRow.id,
                                      "value",
                                      e.target.value
                                    )
                                  }
                                />
                              </StyledTableCellHistory>

                              <StyledTableCellHistory sx={{ width: "25%" }}>
                                <Autocomplete
                                  freeSolo
                                  options={[...new Set(unitOptions)].filter(
                                    (u) => u !== null && u !== undefined && String(u).trim() !== ""
                                  )}
                                  value={testRow.unit || ""}
                                  onChange={(_, newValue) =>
                                    handleTestChange(
                                      panelIndex,
                                      testRow.id,
                                      "unit",
                                      newValue ?? ""
                                    )
                                  }
                                  onInputChange={(_, newInputValue, reason) => {
                                    // keep state in sync when user types a custom unit
                                    if (reason === "input" || reason === "clear") {
                                      handleTestChange(
                                        panelIndex,
                                        testRow.id,
                                        "unit",
                                        newInputValue ?? ""
                                      );
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <StyledTextField
                                      {...params}
                                      size="small"
                                      fullWidth
                                      placeholder="Select Unit"
                                    />
                                  )}
                                  sx={{
                                    "& .MuiInputBase-root": {
                                      height: 50, // fixed height (match previous Select)
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      textOverflow: "ellipsis",
                                    },
                                  }}
                                />
                              </StyledTableCellHistory>

                              <StyledTableCellHistory sx={{ width: "5%" }}>
                                <Tooltip title="Delete Test">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTest(panelIndex, testRow.id);
                                    }}
                                  >
                                    <Icon
                                      name="deleteTestInDiagnostic"
                                      size={20}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </StyledTableCellHistory>
                            </StyledTableRowHistory>
                          ))}
                        </TableBody>
                      </Table>
                    </StyledTableContainer>

                    <div style={{ marginTop: 8, float: "right" }}>
                      {/* <button onClick={() => handleAddTest(panelIndex)}>
                        Add Test
                      </button> */}
                      <StyledButton
                        bgColor="#125386"
                        onClick={() => handleAddTest(panelIndex)}
                        variant="contained"
                        sx={{
                          fontSize: { xs: "0.6rem", sm: "0.8rem" },
                          width: "120%",
                        }}
                      >
                        Add Test
                      </StyledButton>
                    </div>
                  </AccordionDetails>
                </UnderwriterStyledAccordion>
              </div>
            ))}
          </Paper>
        </ThemeProvider>
      )}
    </div>
  );
};

export default DiagnosticReportsUnderwriter;
